import { Link } from 'react-router-dom';
import { ArrowRight, Users, Award, Briefcase, Monitor, BookOpen, CheckCircle, Cloud, Database, Settings, Layers, Brain, Quote, Star, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { trainingTracks } from '@/lib/api';

// Training Categories - Easy to expand
const trainingCategories = [
  {
    id: 'erp',
    name: 'ERP Systems',
    icon: Database,
    description: 'Master enterprise resource planning platforms for business operations',
    color: 'from-teal-500 to-teal-700',
    bgColor: 'bg-teal-50',
    iconColor: 'text-teal-600',
    programs: ['IFS', 'Odoo', 'Oracle', 'NetSuite', 'Infor'],
    href: '/programs',
    badge: '5 Programs'
  },
  {
    id: 'ai',
    name: 'AI Technologies',
    icon: Brain,
    description: '5 live AI certifications (Microsoft + NVIDIA) with 1250+ exam-grade questions and AI Coach',
    color: 'from-violet-500 to-purple-700',
    bgColor: 'bg-violet-50',
    iconColor: 'text-violet-600',
    programs: ['AI-900', 'AI-102', 'DP-100', 'AB-900', 'NCA-GENL'],
    href: '/microsoft-track',
    badge: '5 Certs Live'
  },
  {
    id: 'cloud',
    name: 'Cloud & DevOps',
    icon: Cloud,
    description: 'Build expertise in cloud infrastructure and DevOps practices',
    color: 'from-blue-500 to-blue-700',
    bgColor: 'bg-blue-50',
    iconColor: 'text-blue-600',
    programs: ['AZ-900', 'AWS Cloud Practitioner'],
    href: '/az-900-exam-prep',
    badge: 'AZ-900 Live'
  },
  {
    id: 'security',
    name: 'Security & Identity',
    icon: Shield,
    description: 'Microsoft Entra identity, access management, and Conditional Access mastery',
    color: 'from-indigo-500 to-blue-700',
    bgColor: 'bg-indigo-50',
    iconColor: 'text-indigo-600',
    programs: ['SC-300'],
    href: '/sc-300-exam-prep',
    badge: 'SC-300 Live'
  },
  {
    id: 'business',
    name: 'Business Software',
    icon: Settings,
    description: 'Specialize in leading CRM and service management platforms',
    color: 'from-purple-500 to-purple-700',
    bgColor: 'bg-purple-50',
    iconColor: 'text-purple-600',
    programs: ['Salesforce', 'ServiceNow'],
    href: '/business-software',
    badge: 'Coming Soon'
  }
];

const valueProps = [
  { icon: Monitor, title: '100% Live Online', description: 'Real-time instructor-led training with interactive Q&A sessions' },
  { icon: BookOpen, title: 'Hands-On Labs', description: 'Practice on real systems with guided exercises and projects' },
  { icon: Award, title: 'Certification Ready', description: 'Curriculum aligned with official certification exams' },
  { icon: Briefcase, title: 'Job-Ready Skills', description: 'Gain practical skills employers actively seek' },
];

const stats = [
  { number: '5,000+', label: 'Professionals Trained' },
  { number: '95%', label: 'Certification Success' },
  { number: '50+', label: 'Corporate Clients' },
  { number: '4.9/5', label: 'Student Rating' },
];

const trustedBy = ['Fortune 500 Companies', 'Tech Startups', 'Government Agencies', 'Healthcare Organizations'];

export default function HomePage() {
  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section - Technology Agnostic */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-teal-50/30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <span className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-medium mb-6">
                AI-Powered Cert prep + ERP Consultant career tracks · Live, on-demand, or team
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
                Land the <span className="text-teal-600">cert</span>. Land the <span className="text-orange-500">role</span>.
              </h1>
              <p className="text-lg text-slate-600 mb-8 max-w-2xl">
                Prepare for <span className="font-semibold text-slate-800">AWS, Microsoft, Google Cloud, and NVIDIA</span> certifications with AI-powered coaching and realistic practice exams — and/or build an ERP consulting career in <span className="font-semibold text-slate-800">IFS, Oracle, NetSuite, Odoo, and Infor</span> through expert-led training.
              </p>
              <div className="flex flex-wrap gap-3 items-center">
                <Link to="/microsoft-track">
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8 py-3 flex items-center space-x-2" data-testid="hero-browse-certs-btn">
                    <span>Browse certs</span>
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/programs">
                  <Button variant="outline" className="rounded-full px-8 py-3 border-slate-300 hover:border-teal-500 hover:text-teal-700" data-testid="hero-erp-tracks-btn">
                    See ERP career tracks
                  </Button>
                </Link>
                <Link to="/contact" className="text-sm font-medium text-slate-600 hover:text-teal-700 ml-1 underline-offset-4 hover:underline" data-testid="hero-talk-advisor-btn">
                  Talk to an advisor →
                </Link>
              </div>
              
              {/* Trust Indicators */}
              <div className="mt-10 pt-8 border-t border-slate-200">
                <p className="text-sm text-slate-500 mb-3">Trusted by professionals from</p>
                <div className="flex flex-wrap gap-4">
                  {trustedBy.map((item) => (
                    <span key={item} className="text-sm text-slate-600 bg-slate-100 px-3 py-1 rounded-full">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="relative animate-fade-in-up animate-delay-200">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1531482615713-2afd69097998?w=800&h=600&fit=crop" 
                  alt="Professional tech training"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white/90 backdrop-blur-md p-5 rounded-xl shadow-lg border border-slate-100">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">5,000+</p>
                    <p className="text-sm text-slate-600">Professionals Trained</p>
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 bg-white/90 backdrop-blur-md p-4 rounded-xl shadow-lg border border-slate-100">
                <div className="flex items-center space-x-2">
                  <Award className="w-5 h-5 text-teal-600" />
                  <span className="text-sm font-medium text-slate-700">95% Pass Rate</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Training Categories Grid - Main Feature */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Training Programs by Category
            </h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              Choose your learning path across enterprise technology domains
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {trainingCategories.map((category) => (
              <Link key={category.id} to={category.href}>
                <Card className={`h-full border-2 border-transparent hover:border-teal-200 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 overflow-hidden group ${category.badge === 'Coming Soon' ? 'opacity-90' : ''}`} data-testid={`category-${category.id}`}>
                  <CardContent className="p-8">
                    <div className="flex items-start justify-between mb-6">
                      <div className={`w-16 h-16 ${category.bgColor} rounded-2xl flex items-center justify-center`}>
                        <category.icon className={`w-8 h-8 ${category.iconColor}`} />
                      </div>
                      <span className={`text-xs font-medium px-3 py-1 rounded-full ${
                        category.badge === 'Coming Soon' 
                          ? 'bg-slate-100 text-slate-600' 
                          : 'bg-teal-100 text-teal-700'
                      }`}>
                        {category.badge}
                      </span>
                    </div>
                    
                    <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-teal-700 transition-colors">
                      {category.name}
                    </h3>
                    <p className="text-slate-600 text-sm mb-6">
                      {category.description}
                    </p>
                    
                    <div className="flex flex-wrap gap-2 mb-6">
                      {category.programs.map((program) => (
                        <span key={program} className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">
                          {program}
                        </span>
                      ))}
                    </div>
                    
                    <div className="flex items-center text-teal-600 text-sm font-medium group-hover:translate-x-1 transition-transform">
                      <span>View Programs</span>
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          
          {/* Coming Soon Notice */}
          <div className="mt-12 text-center">
            <p className="text-slate-500 text-sm">
              <Layers className="w-4 h-4 inline mr-1" />
              More training categories coming soon. <Link to="/contact" className="text-teal-600 hover:underline">Get notified</Link> when new programs launch.
            </p>
          </div>
        </div>
      </section>

      {/* Value Props Section */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">Why Choose LearnQuanta?</h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              Industry-leading training methodology designed for real-world success
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {valueProps.map((prop) => (
              <Card key={prop.title} className="bg-white border border-slate-100 shadow-sm hover:shadow-lg p-6 rounded-xl transition-all hover:-translate-y-1">
                <CardContent className="pt-6">
                  <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-4">
                    <prop.icon className="w-7 h-7 text-teal-700" />
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">{prop.title}</h3>
                  <p className="text-slate-600 text-sm">{prop.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Training Tracks Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">Training Tracks for Every Level</h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              Choose the learning path that matches your experience and career goals
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {Object.entries(trainingTracks).map(([key, track]) => (
              <Card key={key} className="bg-white border border-slate-200 shadow-sm hover:shadow-lg rounded-xl relative overflow-hidden transition-all hover:-translate-y-1">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-teal-500 to-teal-700" />
                <CardContent className="pt-8 pb-6 px-6">
                  <span className="text-4xl mb-4 block">{track.icon}</span>
                  <h3 className="font-bold text-xl text-slate-900 mb-1">{track.name}</h3>
                  <p className="text-teal-600 text-sm font-medium mb-4">{track.subtitle}</p>
                  <p className="text-slate-600 text-sm mb-6">{track.description}</p>
                  <ul className="space-y-2 mb-6">
                    {track.features.slice(0, 4).map((feature, i) => (
                      <li key={i} className="flex items-start space-x-2 text-sm text-slate-600">
                        <CheckCircle className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-slate-500">Duration: <span className="font-medium">{track.duration}</span></p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 lg:py-28 bg-teal-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-4xl lg:text-5xl font-bold text-white mb-2">{stat.number}</p>
                <p className="text-teal-200">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured ERP Programs */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">Popular ERP Programs</h2>
              <p className="text-slate-600">Our most in-demand training programs</p>
            </div>
            <Link to="/programs" className="hidden md:flex items-center text-teal-600 font-medium hover:text-teal-700">
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {['IFS', 'Odoo', 'Oracle', 'NetSuite', 'Infor'].map((erp) => (
              <Link key={erp} to={`/programs/${erp.toLowerCase()}`}>
                <Card className="bg-white border border-slate-100 shadow-sm hover:shadow-lg p-6 rounded-xl transition-all hover:-translate-y-1 text-center group" data-testid={`featured-${erp.toLowerCase()}`}>
                  <CardContent className="pt-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <Database className="w-6 h-6 text-teal-600" />
                    </div>
                    <h3 className="font-bold text-lg text-slate-900 group-hover:text-teal-700">{erp}</h3>
                    <p className="text-xs text-slate-500 mt-1">ERP Training</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          
          <div className="mt-8 text-center md:hidden">
            <Link to="/programs">
              <Button variant="outline" className="rounded-full">View All Programs</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 lg:py-28 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <span className="inline-block px-4 py-1.5 bg-teal-50 text-teal-700 rounded-full text-sm font-medium mb-4">Student Stories</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">Trusted by professionals from teams that ship</h2>
            <p className="text-lg text-slate-600">From first-time certification candidates to senior architects upskilling for new roles, here&apos;s what learners say after working with our coaches and AI-powered prep.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                quote: 'I passed AI-900 in three weeks while juggling a full-time PM role. The AI Coach Chat felt like having a private tutor — it explained exactly why I got each tricky question wrong.',
                name: 'Priya Subramanian',
                role: 'Senior Product Manager',
                company: 'Fintech (Bangalore)',
                cert: 'AI-900',
                rating: 5,
              },
              {
                quote: 'The exam simulations are uncannily close to the real thing. I came out of AZ-900 thinking "wait, that was easier than the LearnQuanta sim." Cleared 870/1000.',
                name: 'Marcus Hale',
                role: 'Cloud Engineer',
                company: 'Healthcare SaaS (Austin, TX)',
                cert: 'AZ-900',
                rating: 5,
              },
              {
                quote: 'Our IFS Cloud Financials cohort moved 11 of 14 consultants to certified status in one quarter. The instructor-led sessions plus weak-area drills were a force multiplier for our team.',
                name: 'Anita Verma',
                role: 'Practice Lead, ERP',
                company: 'Global SI (Mumbai)',
                cert: 'IFS Cloud Financials',
                rating: 5,
              },
              {
                quote: 'DP-100 was the steepest curve I\'ve faced. The MLOps deployment questions and the responsible-AI explanations turned out to be exactly what I needed for my Azure ML Engineer interview loop.',
                name: 'Kenji Watanabe',
                role: 'Data Scientist',
                company: 'Logistics (Tokyo)',
                cert: 'DP-100',
                rating: 5,
              },
              {
                quote: 'I\'ve tried three other Oracle Cloud SCM courses. This one finally connected the dots between Procurement, Inventory, and Order Management with real client scenarios.',
                name: 'Diana Okafor',
                role: 'Oracle SCM Consultant',
                company: 'Manufacturing (Lagos → Toronto)',
                cert: 'Oracle Cloud SCM',
                rating: 5,
              },
              {
                quote: 'AI-102 felt much more applied than I expected — the prompt engineering and RAG sections directly improved how we ship Copilot features at work. Money well spent.',
                name: 'Lukas Becker',
                role: 'Senior Software Engineer',
                company: 'Enterprise SaaS (Berlin)',
                cert: 'AI-102',
                rating: 5,
              },
            ].map((t, idx) => (
              <Card key={idx} className="border-slate-200 hover:shadow-lg transition-shadow" data-testid={`testimonial-${idx}`}>
                <CardContent className="p-6">
                  <Quote className="w-7 h-7 text-teal-500 mb-3" />
                  <div className="flex items-center mb-3" aria-label={`${t.rating} out of 5 stars`}>
                    {[...Array(t.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 leading-relaxed mb-5">{t.quote}</p>
                  <div className="border-t border-slate-100 pt-4 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-slate-900">{t.name}</div>
                      <div className="text-xs text-slate-500">{t.role} · {t.company}</div>
                    </div>
                    <span className="text-xs px-2.5 py-1 rounded-full bg-violet-100 text-violet-700 font-medium">{t.cert}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-14 max-w-4xl mx-auto">
            {[
              { value: '15,000+', label: 'Active learners' },
              { value: '89%', label: 'First-attempt pass rate' },
              { value: '500K+', label: 'Questions answered' },
              { value: '4.9/5', label: 'Average rating' },
            ].map((s) => (
              <div key={s.label} className="text-center p-4 rounded-xl bg-slate-50">
                <div className="text-2xl sm:text-3xl font-bold text-slate-900">{s.value}</div>
                <div className="text-xs sm:text-sm text-slate-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Advance Your Tech Career?
            </h2>
            <p className="text-lg text-slate-300 mb-8">
              Join thousands of professionals who have transformed their careers with our 
              expert-led training programs. Start your journey today.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/webinar">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8 py-3" data-testid="cta-webinar-btn">
                  Join Free Webinar
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="rounded-full px-8 py-3 border-slate-500 text-white hover:bg-white/10" data-testid="cta-consultation-btn">
                  Request Consultation
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
